import { IonSpinner } from '@ionic/react';
import {
  type ReactNode,
  Suspense,
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
} from 'react';

import { useTenantQuery } from '~/api/hooks/useTenantQuery';
import { type Tenant, isUnknownTenant } from '~/api/tenant';
import { type BoundaryFailure, ErrorBoundary } from '~/components/ErrorBoundary';
import { TENANT_HOST } from '~/config';
import { useDocumentTitle } from '~/hooks/useDocumentTitle';
import { TenantUnavailable } from '~/pages/tenant/TenantUnavailable';
import { TenantUnknown } from '~/pages/tenant/TenantUnknown';
import { forgetTenantOfOtherHost } from '~/stores/tenant';
import { applyTenantTheme } from '~/theme/tenantTheme';

const TenantContext = createContext<Tenant | null>(null);

interface TenantProviderProps {
  tenant: Tenant;
  children: ReactNode;
}

export function TenantProvider({ tenant, children }: TenantProviderProps): JSX.Element {
  return <TenantContext.Provider value={tenant}>{children}</TenantContext.Provider>;
}

/** O escritório do endereço. Só existe abaixo do `TenantGate`, que não renderiza nada sem ele. */
export function useTenant(): Tenant {
  const tenant = useContext(TenantContext);
  if (!tenant) {
    throw new Error('useTenant fora do TenantGate');
  }
  return tenant;
}

function renderTenantFailure({ error, retry }: BoundaryFailure): ReactNode {
  return isUnknownTenant(error) ? <TenantUnknown /> : <TenantUnavailable onRetry={retry} />;
}

function useForgetTenantOfOtherHost(host: string): void {
  useEffect(() => {
    forgetTenantOfOtherHost(host);
  }, [host]);
}

/**
 * Pinta o app com as cores do escritório. `useLayoutEffect` roda antes de o navegador pintar a
 * primeira tela, então nenhuma tela aparece com a marca padrão; uma revalidação que muda as cores
 * reaplica.
 */
function useTenantTheme({ cor_primaria, cor_secundaria }: Tenant): void {
  useLayoutEffect(() => {
    applyTenantTheme({ cor_primaria, cor_secundaria });
  }, [cor_primaria, cor_secundaria]);
}

function TenantLoading(): JSX.Element {
  return (
    <output className="rx-container flex flex-1 items-center justify-center">
      <IonSpinner name="dots" aria-hidden="true" />
      <span className="sr-only">Carregando</span>
    </output>
  );
}

interface ResolvedTenantProps {
  host: string;
  children: ReactNode;
}

function ResolvedTenant({ host, children }: ResolvedTenantProps): JSX.Element {
  const tenant = useTenantQuery(host);
  useTenantTheme(tenant);
  useDocumentTitle(tenant.nome);

  return (
    <TenantProvider tenant={tenant}>
      {/* Erro de uma tela não é erro do escritório: para aqui, antes da fronteira do gate. */}
      <ErrorBoundary>{children}</ErrorBoundary>
    </TenantProvider>
  );
}

interface TenantGateProps {
  children: ReactNode;
  /** Host pelo qual o escritório é resolvido; o padrão é o do build ou o da janela. */
  host?: string;
}

/**
 * Resolve o escritório pelo host antes de renderizar o app. Com um escritório lembrado para o
 * mesmo host, abre na hora e revalida em segundo plano; sem ele, espera o backend e mostra
 * `TenantUnknown` (endereço sem escritório) ou `TenantUnavailable` (sem resposta, com retry).
 */
export function TenantGate({ children, host = TENANT_HOST }: TenantGateProps): JSX.Element {
  useForgetTenantOfOtherHost(host);

  return (
    <ErrorBoundary fallback={renderTenantFailure}>
      <Suspense fallback={<TenantLoading />}>
        <ResolvedTenant host={host}>{children}</ResolvedTenant>
      </Suspense>
    </ErrorBoundary>
  );
}
